import { LeadPaymentDetail, PatchProfilePaymentInput } from 'models/Lead';

const ACCOUNT_NUMBER_PATTERN = /^\d{4,20}$/;

/** Digits only: spaces and hyphens are dropped; blank becomes null. */
export const normalizeAccountNumber = (
  raw: string | null | undefined
): string | null => {
  const digits = (raw ?? '').replace(/[\s-]/g, '');
  return digits.length > 0 ? digits : null;
};

/** 4 to 20 digits once normalized; an empty value is valid (it clears). */
export const isValidAccountNumber = (raw: string | null | undefined) => {
  const digits = normalizeAccountNumber(raw);
  return digits === null || ACCOUNT_NUMBER_PATTERN.test(digits);
};

export const maskAccountNumber = (value: string | null | undefined) =>
  value ? `•••• ${value.slice(-4)}` : '';

export interface PaymentAccountValues {
  bankId: number | null;
  accountTypeId: number | null;
  accountNumber: string | null;
  pensionFundId: number | null;
  healthProviderId: number | null;
}

export interface PaymentShippingValues {
  shippingSameAsResidence: boolean;
  shippingStreet: string | null;
  shippingNumber: string | null;
  shippingUnit: string | null;
  // UI-only: drives the region → comuna cascade, never sent.
  shippingRegionId: string | null;
  shippingCityId: number | null;
}

export const getPaymentAccountValues = (
  payment: LeadPaymentDetail
): PaymentAccountValues => ({
  bankId: payment.bank?.id ?? null,
  accountTypeId: payment.accountType?.id ?? null,
  accountNumber: payment.accountNumber ?? null,
  pensionFundId: payment.pensionFund?.id ?? null,
  healthProviderId: payment.healthProvider?.id ?? null,
});

export const getPaymentShippingValues = (
  payment: LeadPaymentDetail
): PaymentShippingValues => ({
  shippingSameAsResidence: payment.shipping.sameAsResidence,
  shippingStreet: payment.shipping.street ?? null,
  shippingNumber: payment.shipping.number ?? null,
  shippingUnit: payment.shipping.unit ?? null,
  shippingRegionId: payment.shipping.city?.regionId ?? null,
  shippingCityId: payment.shipping.city?.id ?? null,
});

type PaymentPatch = Omit<PatchProfilePaymentInput, 'section'>;
type PaymentFormValues = Partial<PaymentAccountValues & PaymentShippingValues>;

const UI_ONLY_KEYS = new Set<string>(['shippingRegionId']);

const normalizeValue = (key: string, value: unknown) => {
  if (key === 'accountNumber') return normalizeAccountNumber(value as string);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  return value ?? null;
};

/**
 * PATCH keys for the fields that changed. Omitted keys stay unchanged
 * upstream; a cleared field (undefined, blank) is sent as null.
 */
export const buildPaymentPatch = (
  current: PaymentFormValues,
  next: PaymentFormValues
): PaymentPatch => {
  const patch: Record<string, unknown> = {};
  Object.keys(current).forEach((key) => {
    if (UI_ONLY_KEYS.has(key)) return;
    const before = normalizeValue(key, (current as any)[key]);
    const after = normalizeValue(key, (next as any)[key]);
    if (before !== after) patch[key] = after;
  });
  return patch as PaymentPatch;
};

// Backend codes that point at one field of the form.
const ERROR_FIELDS: Record<string, keyof PaymentPatch> = {
  'admin.bank_not_found': 'bankId',
  'admin.account_type_not_found': 'accountTypeId',
  'admin.pension_fund_not_found': 'pensionFundId',
  'admin.health_provider_not_found': 'healthProviderId',
  'admin.shipping_city_not_found': 'shippingCityId',
  'admin.invalid_account_number': 'accountNumber',
};

export const getPaymentErrorField = (
  code: string | undefined
): keyof PaymentPatch | null => (code && ERROR_FIELDS[code]) || null;
